import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Check, ArrowRight, Info, Wine } from "lucide-react";
import { toast } from "sonner";

const CAT_LABEL = {
  starters: "Starters",
  mains: "Mains",
  sides: "Sides & breads",
  desserts: "Desserts",
  drinks_non_alc: "Drinks",
  drinks_alc: "Wine, beer & cocktails",
};
const CAT_ORDER = ["starters", "mains", "sides", "desserts", "drinks_non_alc", "drinks_alc"];

// Normalise a rule into a single integer cap.
// Supports: number (e.g. 3), {total: 3}, {veg: 3, non_veg: 1}.
function ruleToCap(rule) {
  if (rule == null) return 0;
  if (typeof rule === "number") return rule;
  if (typeof rule === "object") {
    if (rule.total != null) return Number(rule.total) || 0;
    return (Number(rule.veg) || 0) + (Number(rule.non_veg) || 0);
  }
  return 0;
}

export default function PackageSelection() {
  const { bookingId } = useParams();
  const nav = useNavigate();
  const [booking, setBooking] = useState(null);
  const [pkg, setPkg] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [picks, setPicks] = useState(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: b } = await api.get(`/bookings/${bookingId}`);
        setBooking(b);
        if (!b.package) {
          toast.error("This booking is not on a package");
          nav(`/checkout/${bookingId}`);
          return;
        }
        const { data: rest } = await api.get(`/restaurants/${b.restaurant.id}`);
        const fullPkg = rest.packages.find((p) => p.id === b.package.id) || b.package;
        setPkg(fullPkg);
        // If the package declares a curated subset of dishes, only show those.
        const allowed = fullPkg.menu_item_ids && fullPkg.menu_item_ids.length > 0
          ? new Set(fullPkg.menu_item_ids)
          : null;
        setMenuItems(allowed ? rest.menu_items.filter((m) => allowed.has(m.id)) : rest.menu_items);
      } catch (e) {
        toast.error("Could not load");
        nav("/home");
      }
    })();
  }, [bookingId]); // eslint-disable-line

  // Group menu items by category (single list per category — veg dot stays on the dish).
  const grouped = useMemo(() => {
    const out = {};
    for (const m of menuItems) {
      out[m.category] = out[m.category] || [];
      out[m.category].push(m);
    }
    return out;
  }, [menuItems]);

  // Per-category total pick count.
  const counts = useMemo(() => {
    const out = {};
    for (const id of picks) {
      const m = menuItems.find((x) => x.id === id);
      if (!m) continue;
      out[m.category] = (out[m.category] || 0) + 1;
    }
    return out;
  }, [picks, menuItems]);

  const togglePick = (m, cap) => {
    const next = new Set(picks);
    if (next.has(m.id)) { next.delete(m.id); setPicks(next); return; }
    const got = counts[m.category] || 0;
    if (got >= cap) {
      toast.error(`This package allows only ${cap} ${m.category.replace("_", " ")}.`);
      return;
    }
    next.add(m.id);
    setPicks(next);
  };

  // Validation: every category's total picks must match its cap exactly.
  const allFilled = useMemo(() => {
    if (!pkg?.selection_rules) return false;
    for (const [cat, rule] of Object.entries(pkg.selection_rules)) {
      const cap = ruleToCap(rule);
      if (cap === 0) continue;
      if ((counts[cat] || 0) !== cap) return false;
    }
    return true;
  }, [pkg, counts]);

  const confirm = async () => {
    setSaving(true);
    try {
      await api.post(`/bookings/${bookingId}/package-selection`, {
        items: Array.from(picks).map((id) => ({ menu_item_id: id })),
      });
      toast.success("Dish selection saved");
      nav(`/checkout/${bookingId}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  if (!booking || !pkg) return <div className="py-10 text-light-ink animate-pulse">Loading...</div>;

  return (
    <div className="space-y-6 animate-fade-up pb-40">
      <header>
        <p className="text-amber text-xs uppercase tracking-[0.18em] font-medium mb-2">{pkg.name}</p>
        <h1 className="font-serif text-3xl sm:text-4xl leading-tight">
          Pick your dishes for {booking.pax} guests.
        </h1>
        <p className="text-light-ink text-sm mt-2">
          Choose the dishes — {booking.restaurant?.name}'s chef decides portions for {booking.pax} based on industry standards. No quantity choices here.
        </p>
        <div className="mt-3 bg-amber-soft/20 border border-amber-soft/50 rounded-xl p-3 flex gap-2 text-sm" data-testid="pkg-sel-helper">
          <Info className="w-4 h-4 text-amber mt-0.5 shrink-0" strokeWidth={1.6}/>
          <p className="text-ink">{pkg.inclusions.join(" · ")}</p>
        </div>
      </header>

      {CAT_ORDER.filter((c) => ruleToCap(pkg.selection_rules?.[c]) > 0).map((cat) => {
        const cap = ruleToCap(pkg.selection_rules[cat]);
        const got = counts[cat] || 0;
        const isAlc = cat === "drinks_alc";
        const items = grouped[cat] || [];
        return (
          <div key={cat} data-testid={`pkg-sel-cat-${cat}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {isAlc && <Wine className="w-4 h-4 text-amber" strokeWidth={1.6}/>}
                <p className="text-xs uppercase tracking-[0.18em] text-amber font-medium">{CAT_LABEL[cat]}</p>
              </div>
              <div className="text-right text-[11px] uppercase tracking-wider text-light-ink">
                <span className={got === cap ? "text-green-700 font-medium" : "text-ink font-medium"}>
                  {got}/{cap} picked
                </span>
              </div>
            </div>

            {items.length === 0 ? (
              <div className="tablo-card p-4 text-sm text-light-ink italic">No dishes offered in this package for this category.</div>
            ) : (
              <div className="tablo-card divide-y divide-tablo-border/60">
                {items.map((m) => {
                  const selected = picks.has(m.id);
                  return (
                    <button
                      key={m.id}
                      onClick={() => togglePick(m, cap)}
                      className={`w-full flex items-center justify-between p-4 text-left transition-colors ${selected ? "bg-amber-soft/15" : "hover:bg-cream"}`}
                      data-testid={`pkg-pick-${m.id}`}
                    >
                      <div className="flex items-start gap-2 flex-1 min-w-0">
                        <span className={`mt-1.5 inline-block w-2 h-2 rounded-full shrink-0 ${m.veg_flag ? "bg-green-600" : "bg-rose-600"}`}/>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-ink truncate">{m.name}</p>
                          {m.must_try && (
                            <span className="text-[10px] uppercase tracking-wider bg-amber/30 text-ink px-1.5 py-0.5 rounded-full font-medium">★ Chef's pick</span>
                          )}
                        </div>
                      </div>
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 border-2 ${selected ? "bg-ink border-ink" : "border-tablo-border"}`}>
                        {selected && <Check className="w-3.5 h-3.5 text-cream" strokeWidth={3}/>}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Sticky confirm bar */}
      <div className="fixed left-0 right-0 bottom-16 sm:bottom-20 z-30 px-5 pointer-events-none">
        <div className="max-w-3xl mx-auto tablo-card p-4 pointer-events-auto shadow-tablo-lg">
          <div className="flex items-baseline justify-between gap-3 mb-2">
            <p className="text-xs uppercase tracking-wider text-light-ink">
              {pkg.name} · ₹{pkg.price_per_head_inr}/head × {booking.pax} guests
            </p>
            <p className="font-serif text-xl">₹{(pkg.price_per_head_inr * booking.pax).toLocaleString("en-IN")}</p>
          </div>
          <Button onClick={confirm} disabled={!allFilled || saving} className="w-full tablo-btn-primary" data-testid="pkg-sel-confirm-btn">
            {saving ? "Saving..." : allFilled ? "Confirm dishes & continue" : "Pick all required dishes to continue"}
            {allFilled && !saving && <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2}/>}
          </Button>
        </div>
      </div>
    </div>
  );
}
